import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import jsPDF from "jspdf";
import API from "../services/api";
import "./PaymentHistoryPage.css";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-");
const fmtAmt = (n) => (Number.isFinite(Number(n)) ? `Rs ${Number(n).toLocaleString("en-IN")}` : "Rs 0");
const resolveGatewayAmount = (row = {}) => {
  const candidates = [
    row?.amount,
    row?.amountRupees,
    row?.payableAmount,
    row?.totalPayable,
    row?.payment?.latestAmountRupees,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const tone = (s) => {
  const v = String(s || "").toLowerCase();
  if (["success", "released", "approved"].includes(v)) return "success";
  if (["failed", "error", "cancelled", "rejected"].includes(v)) return "danger";
  if (["pending", "created"].includes(v)) return "warning";
  return "secondary";
};

const PaymentHistoryPage = ({
  endpoint,
  title = "Payment History",
  createEndpoint = "",
  statusEndpoint = "",
  ledgerEndpoint = "",
  adminVendorLedgerBase = "",
}) => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialTab = params.get("tab") === "ledger" ? "ledger" : "transactions";
  const initialVendorId = params.get("vendorId") || "";
  const initialPoNumber = params.get("poNumber") || "";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, success: 0, pending: 0, failed: 0 });
  const [q, setQ] = useState("");
  const [payingRef, setPayingRef] = useState("");
  const [statusSyncing, setStatusSyncing] = useState(false);

  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState("");
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState({
    total: 0,
    totalDebit: 0,
    totalCredit: 0,
    closingBalance: 0,
  });
  const [ledgerUser, setLedgerUser] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [vendorId] = useState(initialVendorId);
  const [poNumber] = useState(initialPoNumber);

  const hasLedger = Boolean(ledgerEndpoint || adminVendorLedgerBase);

  const ledgerUrl = useMemo(() => {
    if (ledgerEndpoint) return ledgerEndpoint;
    if (adminVendorLedgerBase && vendorId) {
      return `${adminVendorLedgerBase}/${encodeURIComponent(vendorId)}/ledger`;
    }
    return "";
  }, [ledgerEndpoint, adminVendorLedgerBase, vendorId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(endpoint);
      const list = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setRows(list);
      setSummary(res.data?.summary || { total: list.length, success: 0, pending: 0, failed: 0 });
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load payment history");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const loadLedger = useCallback(async () => {
    if (!ledgerUrl) return;
    setLedgerLoading(true);
    setLedgerError("");
    try {
      const res = await API.get(ledgerUrl, {
        params: {
          from: fromDate || undefined,
          to: toDate || undefined,
          poNumber: poNumber || undefined,
        },
      });
      const list = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setLedgerRows(list);
      setLedgerSummary(
        res.data?.summary || {
          total: list.length,
          totalDebit: 0,
          totalCredit: 0,
          closingBalance: 0,
        }
      );
      setLedgerUser(res.data?.user || null);
    } catch (e) {
      setLedgerError(e?.response?.data?.message || "Failed to load ledger");
      setLedgerRows([]);
    } finally {
      setLedgerLoading(false);
    }
  }, [ledgerUrl, fromDate, toDate, poNumber]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeTab !== "ledger") return;
    if (!ledgerUrl) return;
    loadLedger();
  }, [activeTab, ledgerUrl, loadLedger]);

  useEffect(() => {
    const txnId = new URLSearchParams(window.location.search).get("merchantTransactionId");
    if (!txnId || !statusEndpoint) return;

    let alive = true;
    const syncStatus = async () => {
      try {
        setStatusSyncing(true);
        await API.get(`${statusEndpoint}/${encodeURIComponent(txnId)}`);
      } catch {
        // keep UI usable even if callback status polling fails once
      } finally {
        if (alive) {
          setStatusSyncing(false);
          load();
          const next = new URL(window.location.href);
          next.searchParams.delete("merchantTransactionId");
          window.history.replaceState({}, "", next.toString());
        }
      }
    };

    syncStatus();
    return () => {
      alive = false;
    };
  }, [load, statusEndpoint]);

  const filtered = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      [r.referenceNo, r.purpose, r.gateway, r.status, r.mode, r.user?.fullName, r.userRole, r.amount]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [rows, q]);

  const canStartGateway = Boolean(createEndpoint);
  const canPayRow = (r) => {
    const amount = resolveGatewayAmount(r);
    const status = String(r?.status || "").toLowerCase();
    return canStartGateway && amount > 0 && !["success", "released"].includes(status);
  };

  const payRow = async (r) => {
    if (!canPayRow(r) || payingRef) return;
    const amount = resolveGatewayAmount(r);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Valid payment amount not found.");
      return;
    }

    const purpose = String(r?.purpose || "").trim() || `Payment ${r?.referenceNo || ""}`.trim();

    try {
      setError("");
      setPayingRef(String(r?._id || ""));
      const { data } = await API.post(createEndpoint, { amount, purpose });
      const payUrl = String(data?.redirectUrl || data?.payUrl || "").trim();
      if (!payUrl) throw new Error("Payment link not received");
      window.location.href = payUrl;
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to start PhonePe payment");
    } finally {
      setPayingRef("");
    }
  };

  const downloadLedgerPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const left = 40;
    let y = 46;

    const safeText = (v) => String(v == null ? "" : v);
    const line = (txt, x = left) => {
      doc.text(safeText(txt), x, y);
      y += 16;
    };

    doc.setFontSize(14);
    line(`${title} - Ledger`);
    doc.setFontSize(10);
    line(`Generated: ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`);
    if (ledgerUser?.fullName) line(`Party: ${ledgerUser.fullName}`);
    if (poNumber) line(`PO Filter: ${poNumber}`);
    if (fromDate || toDate) line(`Range: ${fromDate || "Start"} to ${toDate || "Today"}`);
    y += 6;
    line(`Total Entries: ${ledgerSummary.total || 0}`);
    line(`Total Debit: ${fmtAmt(ledgerSummary.totalDebit || 0)}`);
    line(`Total Credit: ${fmtAmt(ledgerSummary.totalCredit || 0)}`);
    line(`Closing Balance: ${fmtAmt(ledgerSummary.closingBalance || 0)}`);
    y += 8;

    doc.setFontSize(9);
    const pageWidth = doc.internal.pageSize.getWidth();
    const dateX = left;
    const partX = 110;
    const refX = 330;
    const debitX = pageWidth - 170;
    const creditX = pageWidth - 115;
    const balX = pageWidth - 60;

    const drawHeader = () => {
      doc.setFont(undefined, "bold");
      doc.text("Date", dateX, y);
      doc.text("Particular", partX, y);
      doc.text("Ref", refX, y);
      doc.text("Debit", debitX, y, { align: "right" });
      doc.text("Credit", creditX, y, { align: "right" });
      doc.text("Bal", balX, y, { align: "right" });
      doc.setFont(undefined, "normal");
      y += 12;
    };

    drawHeader();
    ledgerRows.forEach((r) => {
      if (y > 780) {
        doc.addPage();
        y = 40;
        drawHeader();
      }
      const dateText = fmtDate(r.date).split(",")[0];
      const part = safeText(r.particular || "-").slice(0, 34);
      const ref = safeText(r.ref || "-").slice(0, 12);
      doc.text(dateText, dateX, y);
      doc.text(part, partX, y);
      doc.text(ref, refX, y);
      doc.text(String(Number(r.debit || 0).toFixed(2)), debitX, y, { align: "right" });
      doc.text(String(Number(r.credit || 0).toFixed(2)), creditX, y, { align: "right" });
      doc.text(String(Number(r.balance || 0).toFixed(2)), balX, y, { align: "right" });
      y += 12;
    });

    doc.save(`ledger-${vendorId || "self"}-${Date.now()}.pdf`);
  };

  return (
    <div className="ph-wrap">
      <div className="ph-head">
        <div>
          <h4 className="mb-1">{title}</h4>
          <div className="text-muted small">
            {activeTab === "ledger"
              ? `Date-wise ledger with debit, credit and running balance${poNumber ? ` | PO: ${poNumber}` : ""}`
              : "All recorded payments and transaction status"}
          </div>
        </div>
        <button type="button" className="btn btn-outline-dark btn-sm" onClick={activeTab === "ledger" ? loadLedger : load}>
          Refresh
        </button>
      </div>

      {statusSyncing ? <Alert variant="info">Payment status syncing...</Alert> : null}

      {hasLedger ? (
        <div className="ph-tabs">
          <button type="button" className={activeTab === "transactions" ? "active" : ""} onClick={() => setActiveTab("transactions")}>
            Transactions
          </button>
          <button type="button" className={activeTab === "ledger" ? "active" : ""} onClick={() => setActiveTab("ledger")}>
            Ledger
          </button>
        </div>
      ) : null}

      {activeTab === "transactions" ? (
        <div className="ph-stats">
          <div className="ph-card"><span>Total</span><b>{summary.total || 0}</b></div>
          <div className="ph-card"><span>Success</span><b>{summary.success || 0}</b></div>
          <div className="ph-card"><span>Pending</span><b>{summary.pending || 0}</b></div>
          <div className="ph-card"><span>Failed</span><b>{summary.failed || 0}</b></div>
        </div>
      ) : (
        <div className="ph-stats">
          <div className="ph-card"><span>Total Rows</span><b>{ledgerSummary.total || 0}</b></div>
          <div className="ph-card"><span>Total Debit</span><b>{fmtAmt(ledgerSummary.totalDebit || 0)}</b></div>
          <div className="ph-card"><span>Total Credit</span><b>{fmtAmt(ledgerSummary.totalCredit || 0)}</b></div>
          <div className="ph-card"><span>Closing Balance</span><b>{fmtAmt(ledgerSummary.closingBalance || 0)}</b></div>
        </div>
      )}

      {activeTab === "transactions" ? (
        <div className="ph-search">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by reference, purpose, status, amount..."
          />
        </div>
      ) : (
        <div className="ph-ledger-tools">
          <div className="ph-dates">
            {poNumber ? (
              <label>
                PO
                <input type="text" value={poNumber} readOnly />
              </label>
            ) : null}
            <label>
              From
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadLedger} disabled={!ledgerUrl}>
              Apply
            </button>
          </div>
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={downloadLedgerPdf}
            disabled={!ledgerRows.length}
          >
            Download PDF
          </button>
        </div>
      )}

      {error && activeTab === "transactions" ? <Alert variant="danger">{error}</Alert> : null}
      {ledgerError && activeTab === "ledger" ? <Alert variant="danger">{ledgerError}</Alert> : null}

      {activeTab === "transactions" ? (
        <div className="ph-table-card">
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info" className="m-3">No payment history found.</Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Mode</th>
                    <th>Purpose / Vendor</th>
                    <th>Reference</th>
                    <th>User</th>
                    <th>Gateway</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={`${r.type}_${r._id}`}>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>{fmtAmt(r.amount)}</td>
                      <td><Badge bg={tone(r.status)}>{String(r.status || "pending").toUpperCase()}</Badge></td>
                      <td>{r.type === "gateway" ? "Gateway" : "Manual"}</td>
                      <td>{r.mode || "-"}</td>
                      <td>{r.purpose || "-"}</td>
                      <td className="mono">{r.referenceNo || "-"}</td>
                      <td>{r.user?.fullName || r.userRole || "-"}</td>
                      <td>
                        {canPayRow(r) ? (
                          <Button
                            size="sm"
                            variant="primary"
                            className="ph-pay-btn"
                            disabled={payingRef === String(r._id || "")}
                            onClick={() => payRow(r)}
                          >
                            {payingRef === String(r._id || "") ? "Opening..." : "Pay via PhonePe"}
                          </Button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      ) : (
        <div className="ph-table-card">
          {!ledgerUrl ? (
            <Alert variant="info" className="m-3">Vendor select karke ledger dekhein.</Alert>
          ) : ledgerLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : ledgerRows.length === 0 ? (
            <Alert variant="info" className="m-3">No ledger entries found for selected dates.</Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Particular</th>
                    <th>Ref</th>
                    <th>Type</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.map((r) => (
                    <tr key={`ledger_${r._id}`}>
                      <td>{fmtDate(r.date)}</td>
                      <td>{r.particular || "-"}</td>
                      <td className="mono">{r.ref || "-"}</td>
                      <td>{r.type === "charge" ? "Debit" : "Credit"}</td>
                      <td>{fmtAmt(r.debit)}</td>
                      <td>{fmtAmt(r.credit)}</td>
                      <td><strong>{fmtAmt(r.balance)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryPage;
